import { useEffect, useRef } from "react";
import { parseFontValue } from "../lib/google-fonts";

/**
 * Dynamically injects `<link>` elements for Google Fonts so the branding
 * preview renders the selected typefaces. Deduplicates when heading and body
 * share the same font. Cleans up on unmount.
 */
export function useGoogleFontsPreview(overrides: Record<string, string>): void {
  const prevFamiliesRef = useRef<string[]>([]);

  useEffect(() => {
    const families = new Set<string>();

    for (const key of ["font-heading", "font-body"] as const) {
      const raw = overrides[key];
      if (!raw) continue;
      const parsed = parseFontValue(raw);
      if (parsed) families.add(parsed.family);
    }

    const sorted = [...families].sort();

    // Bail if the set hasn't changed.
    if (
      sorted.length === prevFamiliesRef.current.length &&
      sorted.every((f, i) => f === prevFamiliesRef.current[i])
    ) {
      return;
    }

    prevFamiliesRef.current = sorted;

    // Remove existing preview links.
    document
      .querySelectorAll(`link[data-google-fonts-preview]`)
      .forEach((el) => el.remove());

    // Inject a <link> for each unique family.
    for (const family of sorted) {
      const encoded = encodeURIComponent(family);
      const href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`;

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.setAttribute("data-google-fonts-preview", family);
      document.head.appendChild(link);
    }
  }, [overrides]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      document
        .querySelectorAll(`link[data-google-fonts-preview]`)
        .forEach((el) => el.remove());
    };
  }, []);
}
